import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTab from '../../../../../../../BaseComponents/Tabs/Tab/EDTab';
import EDTabs from '../../../../../../../BaseComponents/Tabs/EDTabs';
import Verifikation from './Components/Verifikation/Verifikation';
import VerifikationQuery, {
  IVerifikation,
  VerifikationStatus,
} from './Queries/VerifikationQuery/VerifikationQuery';
import FakturaBild from '../../../../../../../Components/Fakturabild/Fakturabild';
import VerifikationHeader from './Components/VerifikationHeader/VerifikationHeader';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { capitalize } from '../../../../../../../Utils/stringUtils';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import FunctionMenu from '../../../../../_Common/Components/FunctionMenu/FunctionMenu/FunctionMenu';
import FunctionItem from '../../../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import VerifikationsUnderlag from './Components/VerifikationsUnderlag/VerifikationsUnderlag';
import EkonomiskAnalysVerifikationPrintPDFModal from './Components/EkonomiskAnalysVerifikationPrintPDFModal/EkonomiskAnalysVerifikationPrintPDFModal';
import EkonomiskAnalysVerifikationAttestanterOchMeddelanden from './Components/EkonomiskAnalysVerifikationAttestanterOchMeddelanden/EkonomiskAnalysVerifikationAttestanterOchMeddelanden';
import EkonomiskAnalysKoppladeVerifikationer from './Components/EkonomiskAnalysVerifikationReferenser/EkonomiskAnalysVerifikationReferenser';
import VerifikationStatusMark from './Components/VerifikationStatusMark/VerifikationStatusMark';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { useFrontendUrl } from '../../../../../../../Hooks/useFrontendUrl';
import {
  TEDEKBokfverVERKAT_KODEnum,
  TEDInputSelectionCondition,
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../../../../../globalQueryTypes';
import { getYear } from '../../../../../../../Utils/dateUtils';
import { IBooleanMessage } from '../../Utils/ekonomiskAnalysInitQueryUtils';
import { BFO_OPEN_FROM_EKA } from '../../../../../../../Utils/LocalStorage/localStorageKeys';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

interface IProps {
  open: boolean;
  /** Tab som visas när dialogen öppnas */
  tab?: Tab;
  /** se [Dialog](./?path=/docs/basecomponents-dialog--standard) */
  onClose: () => void;
  /** se [Dialog](./?path=/docs/basecomponents-dialog--standard) */
  onExited?: () => void;
  verifikationBokfdatum?: string;
  verifikationId?: string;
  /** Verifikationsnummer som visas medans verifikationsdata laddas */
  verifikationNr?: string;
  canViewFullVerifikationInfo: boolean;
  backdropInvisible?: boolean;
  indentLevel?: number;
  showOnlyVerificationAttachments?: boolean;
}

export type Tab =
  | 'verifikation'
  | 'fakturabild'
  | 'underlag'
  | 'referenser'
  | 'attestanter';

const openedTab = {
  verifikation: 0,
  fakturabild: 1,
  attestanter: 2,
  underlag: 3,
  referenser: 4,
};

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  tabContainer: {
    backgroundColor: theme.customProperties.panelHeadBg.mediumDark,
    color: theme.palette.primary.contrastText,
    boxShadow: theme.shadows[3],
    display: 'flex',
    flex: '0 1 auto',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  dialogContentWrapper: {
    margin: 0,
    height: '100%',
  },
  dialogPaper: {
    height: '100%',
    width: 'calc(100% - 500px)',
    [theme.breakpoints.up('md')]: {
      maxHeight: 'calc(100% - 16px)',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  dialogPaperScrollPaper: {},
  dialogContent: {
    height: '100%',
  },
  tabsContentContainer: {
    height: '100%',
    overflowY: 'auto',
  },
  scrollOverflowX: {
    overflowX: 'auto',
    overflowY: 'hidden',
    height: '100%',
  },
  disabledTabText: {
    color: theme.customProperties.Fade.LightOnDark.lowEmphasis,
    opacity: 0.5,
  },
  disabledTabTextSelected: {
    color: theme.palette.common.black,
  },
}));

const isHandleVerificationDisabled = (verification: IVerifikation) => {
  if (verification.harAndringsVerifikation) {
    return true;
  }

  switch (verification.kategori) {
    case TEDEKBokfverVERKAT_KODEnum.LR_DEFINITIVBOKNING:
      return false;

    case TEDEKBokfverVERKAT_KODEnum.EK_VERIFIKATIONSREGISTRERING:
      return false;

    case TEDEKBokfverVERKAT_KODEnum.EK_IMPORT_ENLIGT_SIE:
      return false;

    case TEDEKBokfverVERKAT_KODEnum.EK_BOKFORINGSFIL_FRAN_KOB:
      return false;

    case TEDEKBokfverVERKAT_KODEnum.WEK_BOKFORINGSORDER:
      return false;

    default:
      return true;
  }
};

const getDisabledReasonForVer = (
  verification: IVerifikation,
  canHandleBfo: IBooleanMessage | undefined
) => {
  if (canHandleBfo && !canHandleBfo.result) {
    return (
      canHandleBfo.message ??
      'Du kan inte skapa en ändringsverifikation via Ekonomisk Analys'
    );
  }

  if (isHandleVerificationDisabled(verification)) {
    return verification.harAndringsVerifikation
      ? 'Verifikationen har en ändringsverifikation och kan inte hanteras via Bokföringsorder'
      : 'Verifikationskategorin kan inte hanteras via Bokföringsorder';
  }

  return;
};

/**
 * En dialog som visar informationen som ingår i en verifikation från EkonomiskAnalys
 */
export const VerifikationDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const frontendUrl = useFrontendUrl();
  const { classes, cx } = useStyles();
  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [printModalOpen, setPrintModalOpen] = useState<boolean>(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    props.tab != null ? openedTab[props.tab] : 0
  );
  const [prevTab, setPrevTab] = useState(props.tab);
  const [, forceUpdate] = useState<{}>();

  const verifikationID = props.verifikationId;

  const dbName = useMemo(() => {
    return props.showOnlyVerificationAttachments
      ? activeDatabase ?? ''
      : ekonomiskAnalysContext.dbName;
  }, [
    activeDatabase,
    ekonomiskAnalysContext.dbName,
    props.showOnlyVerificationAttachments,
  ]);

  const openPrintModal = () => {
    setPrintModalOpen(true);
  };

  const closePrintModal = () => {
    setPrintModalOpen(false);
  };

  const onResize = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  const onTabChange = (selectedTabIndex: number) => {
    setSelectedTabIndex(selectedTabIndex);
  };

  const getSelectedTabIndex = (
    selectedTabIndex: number,
    hasAttestFlik: boolean,
    inputToMUI: boolean = false
  ) => {
    if (hasAttestFlik || selectedTabIndex < openedTab.attestanter) {
      return selectedTabIndex;
    }

    return selectedTabIndex + (inputToMUI ? -1 : 1);
  };

  if (props.tab !== prevTab) {
    const selectedTabIndex = props.tab != null ? openedTab[props.tab] : 0;

    setSelectedTabIndex(selectedTabIndex);
    setPrevTab(props.tab);
  }

  const customSelection: TEDInputSelectionCondition[][] = [
    [
      {
        namn: 'FiltreraEnligtMinKodbehorighetDatum',
        ass: [],
        typ: TEDSelectionConditionType.Value,
        valueCondition: {
          op: TEDSelectionOperator.equals,
          value: [
            {
              value: props.verifikationBokfdatum
                ? props.verifikationBokfdatum
                : new Date().toLocaleDateString('sv-SE'),
            },
          ],
        },
      },
    ],
  ];

  if (verifikationID) {
    return (
      <VerifikationQuery id={verifikationID} customSelection={customSelection}>
        {({ verifikation, loading, error }) => {
          const hasFakturaBild =
            verifikation != null
              ? verifikation.extendedInfo &&
                verifikation.extendedInfo.fakturaBilder.value.length > 0
              : false;
          const hasUnderlag =
            verifikation != null
              ? verifikation.underlag && verifikation.underlag.length > 0
              : false;
          const hasReferenser = (verifikation?.referenser?.length ?? 0) > 0;

          // Om inte hela verifikationen är färdigladdad så ta vernr från prop
          const title =
            verifikation != null
              ? `Verifikation ${verifikation.standardVerNr}`
              : props.verifikationNr != null
              ? `Verifikation ${props.verifikationNr}`
              : 'Verifikation';

          const subtitle = props.showOnlyVerificationAttachments
            ? `Bilagor från originalverifikation ${
                verifikation?.standardVerNr ?? props.verifikationNr
              }`
            : capitalize(dbName);

          const hasAttestFlik =
            Boolean(verifikation?.extendedInfo?.fakturaNr.value) &&
            Boolean(verifikation?.extendedInfo?.levfakturaId);

          const paddingTop =
            props.indentLevel != null ? props.indentLevel * 60 : undefined;
          const paddingLeft =
            props.indentLevel != null ? props.indentLevel * 32 : undefined;

          const titleIcon =
            verifikation?.backad ||
            verifikation?.harAndringsVerifikation ||
            verifikation?.info?.status.value === VerifikationStatus.SKAPAD ? (
              <VerifikationStatusMark verifikation={verifikation} />
            ) : undefined;
          return (
            <Dialog
              style={{
                paddingTop: paddingTop,
                paddingLeft: paddingLeft,
              }}
              open={props.open}
              onClose={props.onClose}
              title={title}
              titleIcon={titleIcon}
              subtitle={subtitle}
              headerBottomArea={
                !props.showOnlyVerificationAttachments ? (
                  <VerifikationHeader verifikation={verifikation} />
                ) : undefined
              }
              classes={{
                contentWrapper: classes.dialogContentWrapper,
                paper: classes.dialogPaper,
                paperScrollPaper: classes.dialogPaperScrollPaper,
                content: classes.dialogContent,
              }}
              footerActions={[{ label: 'Stäng', onClick: props.onClose }]}
              maxWidth={false}
              backdropInvisible={props.backdropInvisible}
              onExited={props.onExited}
            >
              <React.Fragment>
                <div className={classes.container}>
                  <div className={classes.tabContainer}>
                    <EDTabs
                      value={getSelectedTabIndex(
                        selectedTabIndex,
                        hasAttestFlik,
                        true
                      )}
                      onChange={selectedTabIndex =>
                        onTabChange(
                          getSelectedTabIndex(selectedTabIndex, hasAttestFlik)
                        )
                      }
                      indicatorColor="white"
                      hideIndicator={props.showOnlyVerificationAttachments}
                      noBoxShadow
                    >
                      {
                        <EDTab
                          label="Verifikation"
                          spaciousPadding
                          hidden={props.showOnlyVerificationAttachments}
                        />
                      }
                      {
                        <EDTab
                          label="Fakturabild"
                          disabled={!hasFakturaBild}
                          spaciousPadding
                        />
                      }
                      {hasAttestFlik && (
                        <EDTab
                          label={
                            <>
                              <span>Attest</span>
                              <span
                                className={cx({
                                  [classes.disabledTabText]:
                                    !verifikation?.innehallerAttestMeddelanden,
                                  [classes.disabledTabTextSelected]:
                                    !verifikation?.innehallerAttestMeddelanden &&
                                    selectedTabIndex === openedTab.attestanter,
                                })}
                              >
                                /Meddelande
                              </span>
                            </>
                          }
                          spaciousPadding
                          hidden={props.showOnlyVerificationAttachments}
                        />
                      )}
                      <EDTab
                        label="Underlag"
                        disabled={!hasUnderlag}
                        spaciousPadding
                      />
                      {
                        <EDTab
                          label="Kopplade verifikationer"
                          disabled={
                            !hasReferenser || !props.canViewFullVerifikationInfo
                          }
                          spaciousPadding
                          hidden={props.showOnlyVerificationAttachments}
                        />
                      }
                    </EDTabs>
                    {!loading &&
                      !error &&
                      verifikation &&
                      !props.showOnlyVerificationAttachments && (
                        <FunctionMenu id="verifikationFakturabild" highEmphasis>
                          <FunctionItem
                            iconType="print"
                            onClick={openPrintModal}
                          >
                            Skriv ut
                          </FunctionItem>
                          <FunctionItem
                            disabled
                            disabledReason="Kommande funktionalitet"
                            iconType="event_note"
                          >
                            Visa journal
                          </FunctionItem>

                          <FunctionItem
                            disabled={
                              getDisabledReasonForVer(
                                verifikation,
                                ekonomiskAnalysContext.userInfo?.canHandleBfo
                              ) != undefined
                            }
                            disabledReason={getDisabledReasonForVer(
                              verifikation,
                              ekonomiskAnalysContext.userInfo?.canHandleBfo
                            )}
                            iconType="accounting_record_linked"
                            onClick={() => {
                              localStorage.setItem(BFO_OPEN_FROM_EKA, 'true');
                              window.open(
                                `${frontendUrl}/Rutin/Bokforingsorder/ny?type=BOKFORINGSORDER&variant=VERIFIKATION&bokfar=${getYear(
                                  new Date(
                                    verifikation.info?.bokforingsDatum.value ??
                                      new Date()
                                  )
                                )}&vernr=${verifikation.verNr}`,
                                '_blank'
                              );
                            }}
                          >
                            Hantera Verifikation i Bokföringsorder
                          </FunctionItem>
                        </FunctionMenu>
                      )}
                  </div>
                  {loading && <EDAvenyProgress />}
                  {error && <div>{error.message}</div>}
                  {!loading && !error && verifikation && (
                    <div className={classes.tabsContentContainer}>
                      {selectedTabIndex === openedTab.verifikation && (
                        <div className={classes.scrollOverflowX}>
                          <Verifikation verifikation={verifikation} />
                        </div>
                      )}
                      {selectedTabIndex === openedTab.fakturabild && (
                        <FakturaBild
                          fakturaBilder={
                            verifikation.extendedInfo
                              ? verifikation.extendedInfo.fakturaBilder.value
                              : []
                          }
                          xmlFaktura={
                            verifikation.extendedInfo
                              ? verifikation.extendedInfo.xmlFaktura?.value
                              : false
                          }
                          title={title}
                          xmlFakturaFormat={
                            verifikation?.extendedInfo
                              ? verifikation?.extendedInfo.xmlFormat?.value
                              : undefined
                          }
                          skannadSvefaktura={
                            verifikation?.extendedInfo?.skannadSvefaktura
                              .value || false
                          }
                          verifikationsNr={props.verifikationNr}
                          useWheelToScroll={false}
                          ignoreFloatingMenu
                        />
                      )}
                      {selectedTabIndex === openedTab.attestanter &&
                        verifikation.extendedInfo?.levfakturaId && (
                          <EkonomiskAnalysVerifikationAttestanterOchMeddelanden
                            id={verifikation.extendedInfo?.levfakturaId.value}
                            fakturaNr={
                              verifikation.extendedInfo?.fakturaNr.value
                            }
                            forfalloDatum={
                              verifikation.extendedInfo?.forfalloDatum.value
                            }
                            leverantor={
                              verifikation.extendedInfo?.leverantor.value
                            }
                            messages={verifikation.messages ?? []}
                          />
                        )}
                      {selectedTabIndex === openedTab.underlag &&
                      verifikation ? (
                        <VerifikationsUnderlag
                          title={title}
                          activeDbname={ekonomiskAnalysContext.dbName}
                          bokforingsDatum={
                            verifikation.info?.bokforingsDatum.value ?? ''
                          }
                          verNr={verifikation.verNr}
                          underlag={verifikation.underlag ?? []}
                        />
                      ) : null}
                      {selectedTabIndex === openedTab.referenser &&
                        verifikation && (
                          <EkonomiskAnalysKoppladeVerifikationer
                            referenser={verifikation.referenser ?? []}
                            canViewVerifikationRader={
                              props.canViewFullVerifikationInfo
                            }
                            referenceCount={props.indentLevel ?? 0}
                          />
                        )}
                    </div>
                  )}
                </div>
                {verifikation && (
                  <EkonomiskAnalysVerifikationPrintPDFModal
                    open={printModalOpen}
                    onClose={closePrintModal}
                    verifikation={verifikation}
                    dbName={ekonomiskAnalysContext.dbName}
                    hasFakturaBild={hasFakturaBild ?? false}
                  />
                )}
              </React.Fragment>
            </Dialog>
          );
        }}
      </VerifikationQuery>
    );
  } else {
    return null;
  }
};

export default VerifikationDialog;
